import feedbackSuccessCheck from '../../assets/images/feedback-success-check.svg';
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import type { FlowComponentConfig } from '../types';

/** Compose authored confirmation blocks without changing their actions or order. */
export function FeedbackConfirmationContent({
  components,
  renderBlock,
  componentKey,
  spacing,
}: {
  components: FlowComponentConfig[];
  renderBlock: (component: FlowComponentConfig) => ReactNode;
  componentKey: (component: FlowComponentConfig) => string;
  spacing: number;
}) {
  const render = (component: FlowComponentConfig) => {
    const rendered = renderBlock(
      component.type === 'visualAsset' &&
        component.preset === 'success_check' &&
        !component.url &&
        !component.animation
        ? { ...component, url: feedbackSuccessCheck }
        : component
    );
    if (rendered === null || rendered === undefined || rendered === false) return null;
    return (
      <Box
        sx={
          component.type === 'h1' && (!component.size || component.size === 'standard')
            ? {
                '& h1': {
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: '24px',
                  letterSpacing: 0,
                },
              }
            : component.type === 'subheader'
              ? {
                  '&& [data-flow-component="subheader"] > *': {
                    color: 'var(--cx-brand-text-muted)',
                    lineHeight: component.size === 'compact' ? '22px' : '24px',
                    letterSpacing: 0,
                  },
                }
              : undefined
        }
      >
        {rendered}
      </Box>
    );
  };
  const content: ReactNode[] = [];
  for (let index = 0; index < components.length; index += 1) {
    const component = components[index];
    const heading = components[index + 1];
    const node = render(component);
    if (
      component.type === 'visualAsset' &&
      component.preset === 'success_check' &&
      !component.url &&
      !component.animation &&
      heading?.type === 'h1'
    ) {
      const headingNode = render(heading);
      if (!node || !headingNode) {
        if (node) content.push(<Box key={componentKey(component)}>{node}</Box>);
        if (headingNode) content.push(<Box key={componentKey(heading)}>{headingNode}</Box>);
        index += 1;
        continue;
      }
      const subtitle = components[index + 2];
      const subtitleNode = subtitle?.type === 'subheader' ? render(subtitle) : null;
      content.push(
        <Stack key={componentKey(component)} spacing={0.5}>
          <Box
            data-confirmation-heading-row
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: heading.align ?? 'left',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            <Box sx={{ flexShrink: 0, maxWidth: '100%' }}>{node}</Box>
            <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{headingNode}</Box>
          </Box>
          {subtitleNode}
        </Stack>
      );
      index += subtitle?.type === 'subheader' ? 2 : 1;
    } else if (node) {
      content.push(<Box key={componentKey(component)} data-confirmation-action={component.type === 'actionButton' ? '' : undefined}>{node}</Box>);
    }
  }
  return (
    <Stack data-feedback-confirmation spacing={spacing} sx={{
      '& > [data-confirmation-action] + [data-confirmation-action]': { mt: `${Math.min(spacing * 8, 10)}px` },
    }}>
      {content}
    </Stack>
  );
}
